import { useEffect, useState } from "react";
import { PlaneTakeoff, Wallet, Sparkles, Loader2, Users, Compass, CalendarDays, Zap } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { T, useLang } from "@/i18n";

const CURRENCIES = ["EUR", "USD", "GBP", "CHF", "JPY", "CAD", "AUD", "CNY", "INR", "BRL", "MXN", "SEK", "NOK", "DKK", "PLN", "CZK", "TRY", "AED", "SGD", "HKD", "KRW", "THB", "ZAR", "NZD"];

const CHOICES = [
  { key: "travelers", icon: Users, label: "Chi viaggia?", options: [["solo", "Da solo / Solo travel"], ["couple", "In coppia"], ["family", "Con la famiglia / Figli"], ["friends", "Con amici"]] },
  { key: "vibe", icon: Compass, label: "Tipo di esperienza (Vibe)", options: [["nature", "Natura e Relax (Borghi e montagne)"], ["art", "Arte e Borghi storici"], ["trek", "Trekking e Avventura"], ["food", "Enogastronomia"]] },
  { key: "duration", icon: CalendarDays, label: "Durata", options: [["day", "Fuga di 1 giorno"], ["weekend", "Weekend (2-3 giorni)"], ["week", "Settimana (4-7 giorni)"]] },
];
const EMPTY_CHOICES = { travelers: "", vibe: "", duration: "" };

const PRESETS = {
  it: [
    { id: "preset-grecia", label: "Grecia ad agosto · 7 giorni", prompt: "Vorrei una settimana in Grecia ad agosto con un budget di 1.500€ partendo da Milano", departure: "Milano", budget: "1500", currency: "EUR", travelers: "couple", vibe: "nature", duration: "week" },
    { id: "preset-firenze", label: "Firenze green & arte · 3 giorni", prompt: "3 giorni a Firenze tra arte e natura, spostandomi in treno, cucina tipica e poca folla", departure: "Roma Termini", budget: "500", currency: "EUR", travelers: "couple", vibe: "art", duration: "weekend" },
    { id: "preset-dolomiti", label: "Dolomiti eco-trek · 5 giorni", prompt: "5 giorni di trekking nelle Dolomiti a luglio, rifugi e mezzi pubblici, ritmo tranquillo", departure: "Bologna", budget: "800", currency: "EUR", travelers: "friends", vibe: "trek", duration: "week" },
  ],
  en: [
    { id: "preset-japan", label: "Japan in spring · 7 days", prompt: "7 days in Japan in April for cherry blossom season, trains only, temples and food markets", departure: "New York JFK", budget: "3500", currency: "USD", travelers: "couple", vibe: "art", duration: "week" },
    { id: "preset-lisbon", label: "Lisbon slow weekend · 3 days", prompt: "A slow 3-day weekend in Lisbon with local food, viewpoints and no tourist traps", departure: "London Gatwick", budget: "600", currency: "GBP", travelers: "friends", vibe: "food", duration: "weekend" },
    { id: "preset-peru", label: "Peru & Machu Picchu · 7 days", prompt: "7 days in Peru including Cusco and Machu Picchu, eco-lodges and responsible tours", departure: "Toronto", budget: "4000", currency: "CAD", travelers: "solo", vibe: "trek", duration: "week" },
  ],
};

export const TripForm = ({ onSubmit, loading }) => {
  const { lang, t } = useLang();
  const [prompt, setPrompt] = useState("");
  const [departure, setDeparture] = useState("");
  const [budget, setBudget] = useState("");
  const [choices, setChoices] = useState(EMPTY_CHOICES);
  const [currency, setCurrency] = useState(lang === "en" ? "USD" : "EUR");
  const [currencyTouched, setCurrencyTouched] = useState(false);
  useEffect(() => { if (!currencyTouched) setCurrency(lang === "en" ? "USD" : "EUR"); }, [lang, currencyTouched]);

  const choicesDone = CHOICES.every((c) => choices[c.key]);
  const canSubmit = prompt.trim().length >= 5 && choicesDone && !loading;
  const applyPreset = (p) => {
    setPrompt(p.prompt); setDeparture(p.departure); setBudget(p.budget); setCurrency(p.currency); setCurrencyTouched(true);
    setChoices({ travelers: p.travelers, vibe: p.vibe, duration: p.duration });
  };
  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({ prompt: prompt.trim(), departure: departure.trim() || null, budget: budget.trim() || null, currency, lang, ...choices });
  };

  return (
    <form onSubmit={submit} data-testid="trip-creation-form" className="rounded-3xl border border-emerald-100 bg-white p-6 shadow-[0_30px_80px_-40px_rgba(4,120,87,0.35)] md:p-10">
      <label htmlFor="prompt" className="font-display text-xl font-semibold text-gray-900 sm:text-2xl"><T>Raccontaci il viaggio che hai in mente</T></label>
      <Textarea id="prompt" data-testid="trip-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5}
        placeholder={t("Es. Un weekend in un borgo poco battuto in Italia, natura e relax, partendo da Milano.")}
        className="mt-5 resize-none rounded-2xl border-gray-200 bg-slate-50 p-5 text-base focus-visible:ring-emerald-500" />
      <div className="mt-4 flex flex-wrap gap-2">
        {(PRESETS[lang] || PRESETS.en).map((p) => (
          <button type="button" key={p.id} data-testid={p.id} onClick={() => applyPreset(p)}
            className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-medium text-emerald-800 transition-[background-color,transform] hover:bg-emerald-100 active:scale-95">
            {p.label}
          </button>
        ))}
      </div>
      <div className="mt-8 grid gap-5 md:grid-cols-3">
        {CHOICES.map((c) => (
          <Field key={c.key} icon={c.icon} label={t(c.label)}>
            <select data-testid={`trip-${c.key}-select`} aria-label={t(c.label)} value={choices[c.key]} required
              onChange={(e) => setChoices((s) => ({ ...s, [c.key]: e.target.value }))}
              className={`h-12 w-full rounded-xl border border-input bg-white pl-11 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${choices[c.key] ? "font-semibold text-gray-800" : "text-gray-400"}`}>
              <option value="" disabled>{t("Seleziona...")}</option>
              {c.options.map(([v, l]) => <option key={v} value={v} className="text-gray-800">{t(l)}</option>)}
            </select>
          </Field>
        ))}
      </div>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <Field icon={PlaneTakeoff} label={t("Aeroporto di partenza (opzionale)")}>
          <Input data-testid="trip-departure-input" value={departure} onChange={(e) => setDeparture(e.target.value)} placeholder={t("Es. Milano Malpensa")} className="h-12 rounded-xl pl-11 focus-visible:ring-emerald-500" />
        </Field>
        <Field icon={Wallet} label={t("Budget medio (opzionale)")}>
          <div className="flex gap-2">
            <Input data-testid="trip-budget-input" type="number" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder={t("Es. 1500")} className="h-12 flex-1 rounded-xl pl-11 focus-visible:ring-emerald-500" />
            <select data-testid="trip-currency-select" aria-label={t("Valuta")} value={currency} onChange={(e) => { setCurrency(e.target.value); setCurrencyTouched(true); }}
              className="h-12 rounded-xl border border-input bg-white px-3 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500">
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </Field>
      </div>
      {!choicesDone && prompt.trim().length >= 5 && (
        <p data-testid="trip-choices-hint" className="mt-6 text-sm font-medium text-amber-700"><T>Seleziona chi viaggia, il tipo di esperienza e la durata per continuare.</T></p>
      )}
      <button type="submit" data-testid="submit-trip-button" disabled={!canSubmit}
        className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 px-7 py-4 font-semibold text-white shadow-md transition-[background-color,transform] hover:bg-emerald-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
        {loading ? t("Stiamo creando il tuo viaggio...") : t("Crea il mio itinerario in pochi secondi")}
      </button>
      <p data-testid="submit-trip-safety-note" className="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
        <Zap className="h-3.5 w-3.5 shrink-0 text-emerald-600" /><T>100% gratuito • Nessuna carta di credito o registrazione richiesta.</T>
      </p>
    </form>
  );
};

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-500">{label}</p>
    <div className="relative">
      <Icon className="pointer-events-none absolute left-4 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-emerald-600" />
      {children}
    </div>
  </div>
);
